import { PlotData } from "../types/tender.types";

// Calculate Plot Area based on foundation wall measurements
export const calculatePlotArea = (plot: Partial<PlotData>): number => {
  let plotArea = 0;

  // Get floor areas (Plot Floors + Garage Floors for both floor types)
  if (plot.floorTypes) {
    const beamBlockTotal =
      (plot.floorTypes.beamBlock?.plotFloors || 0) +
      (plot.floorTypes.beamBlock?.garageFloor || 0);
    const insituTotal =
      (plot.floorTypes.insitu?.plotFloors || 0) +
      (plot.floorTypes.insitu?.garageFloor || 0);
    plotArea += beamBlockTotal + insituTotal;
  }

  // External walls × 0.2 (for both Trenchfill and Piled foundations)
  let externalWallsTotal = 0;
  if (plot.externalWalls) {
    externalWallsTotal = Object.values(plot.externalWalls).reduce(
      (sum, val) => sum + (val || 0),
      0
    );
  }
  // For piled foundations, add piled external walls if they exist
  if (plot.piledFoundation?.piledWalls) {
    const piledWalls = plot.piledFoundation.piledWalls;
    externalWallsTotal +=
      (piledWalls.external1100mm || 0) +
      (piledWalls.external500mm || 0) +
      (piledWalls.external450mm || 0);
  }
  plotArea += externalWallsTotal * 0.2;

  // Party walls and Garage External walls × 0.1 (for both Trenchfill and Piled foundations)
  let partyAndGarageTotal = 0;

  // Party walls
  if (plot.partyWalls) {
    partyAndGarageTotal += Object.values(plot.partyWalls).reduce(
      (sum, val) => sum + (val || 0),
      0
    );
  }
  // For piled foundations, add piled party walls if they exist
  if (plot.piledFoundation?.piledWalls) {
    const piledWalls = plot.piledFoundation.piledWalls;
    partyAndGarageTotal +=
      (piledWalls.party750mm || 0) +
      (piledWalls.party500mm || 0) +
      (piledWalls.party450mm || 0);
  }

  // Garage external walls
  if (plot.garageExternalWalls) {
    partyAndGarageTotal += Object.values(plot.garageExternalWalls).reduce(
      (sum, val) => sum + (val || 0),
      0
    );
  }
  // For piled foundations, add piled garage walls if they exist
  if (plot.piledFoundation?.piledWalls) {
    const piledWalls = plot.piledFoundation.piledWalls;
    partyAndGarageTotal +=
      (piledWalls.garExt500mm || 0) +
      (piledWalls.garExt450mm || 0) +
      (piledWalls.garInt500mm || 0) +
      (piledWalls.garInt450mm || 0);
  }

  plotArea += partyAndGarageTotal * 0.1;

  return Math.max(plotArea, 0); // Ensure non-negative value
};

// Enhanced calculation utilities for new foundation features
export const calculatePlotValues = (plot: Partial<PlotData>): Partial<PlotData> => {
  const calculations: Partial<PlotData> = { ...plot };

  // Calculate Plot Area from foundation wall measurements
  calculations.plotArea = parseFloat(calculatePlotArea(plot).toFixed(2));

  // Less Strip (Strip Standard Value) = EGL Schedule - Strip Value
  if (plot.existingGroundLevel && plot.stripStandardValue) {
    calculations.strippedLevel =
      plot.existingGroundLevel - plot.stripStandardValue;
  }

  // Formation Level = If Type is House: FFL - Standard Value for House, If Type is Garage: FFL - Standard Value for Garage
  if (plot.finishedFloorLevel && plot.typeSelection) {
    if (
      plot.typeSelection === "House" &&
      plot.standardValueHouse !== undefined
    ) {
      calculations.formationLevel =
        plot.finishedFloorLevel - plot.standardValueHouse;
    } else if (
      plot.typeSelection === "Garage" &&
      plot.standardValueGarage !== undefined
    ) {
      calculations.formationLevel =
        plot.finishedFloorLevel - plot.standardValueGarage;
    }
  }

  // Depth = Less Strip - Formation Level
  if (calculations.strippedLevel && calculations.formationLevel) {
    calculations.excavationDepth =
      calculations.strippedLevel - calculations.formationLevel;
  }

  // Foundation depth (existing calculation)
  // Use a default founding level if not provided (FFL - 0.9 for standard depth)
  const foundingLevel = plot.foundingLevel || ((plot.finishedFloorLevel || 0) - 0.9);
  if (calculations.formationLevel && foundingLevel) {
    calculations.foundationDepth = calculations.formationLevel - foundingLevel;
  }

  // Cut/Fill calculations - Cut (m³) = If Depth > 0: Depth × Plot Area, else 0; Fill (m³) = If Depth < 0: |Depth| × Plot Area, else 0
  if (calculations.excavationDepth !== undefined && calculations.plotArea) {
    const depth = calculations.excavationDepth;
    if (depth > 0) {
      calculations.cutVolume = depth * calculations.plotArea;
      calculations.fillVolume = 0;
    } else if (depth < 0) {
      calculations.fillVolume = Math.abs(depth) * calculations.plotArea;
      calculations.cutVolume = 0;
    } else {
      calculations.cutVolume = 0;
      calculations.fillVolume = 0;
    }
  }

  // Foundation-specific calculations: FFL - Foundation Standard Value
  // Always initialize calculated fields to 0 first
  calculations.calculatedTrenchfillFounds = 0;
  calculations.calculatedPiledFounds = 0;

  if (plot.finishedFloorLevel) {
    if (
      plot.foundationType === "trenchfill" &&
      plot.trenchfillFoundsStandardValue !== undefined &&
      plot.trenchfillFoundsStandardValue !== null
    ) {
      calculations.calculatedTrenchfillFounds =
        plot.finishedFloorLevel - plot.trenchfillFoundsStandardValue;
    } else if (
      plot.foundationType === "piled" &&
      plot.piledFoundsStandardValue !== undefined &&
      plot.piledFoundsStandardValue !== null
    ) {
      calculations.calculatedPiledFounds =
        plot.finishedFloorLevel - plot.piledFoundsStandardValue;
    }
  }

  // Piled Foundation calculations
  if (plot.foundationType === "piled" && plot.finishedFloorLevel) {
    if (!calculations.piledFoundation) {
      calculations.piledFoundation = {
        topOfBeamAsPiledGA: 0,
        masonryHeight: 0,
        standardConcreteDepth: 0,
        numberOfPiles: 0,
        rebar: 0,
        piledWalls: {
          external1100mm: 0,
          external500mm: 0,
          external450mm: 0,
          party750mm: 0,
          party500mm: 0,
          party450mm: 0,
          intWall500mm: 0,
          tieBeam400x500: 0,
          garExt500mm: 0,
          garExt450mm: 0,
          garInt500mm: 0,
          garInt450mm: 0,
        },
      };
    }

    // Top of Beam As piled GA = FFL - 0.75
    calculations.piledFoundation.topOfBeamAsPiledGA =
      plot.finishedFloorLevel - 0.75;

    // Masonry Height = FFL - Top of Beam As piled GA
    calculations.piledFoundation.masonryHeight =
      plot.finishedFloorLevel - calculations.piledFoundation.topOfBeamAsPiledGA;
  }

  // Additional Trenchfill calculations
  if (
    plot.foundationType === "trenchfill" &&
    plot.foundingDepthSchedule &&
    calculations.calculatedTrenchfillFounds
  ) {
    calculations.additionalDepth =
      calculations.calculatedTrenchfillFounds - plot.foundingDepthSchedule;
    // Always calculate additionalConcrete, even if additionalDepth is 0
    calculations.additionalConcrete =
      calculations.additionalDepth - (plot.additionalBlocks || 0);
  }

  // Second Piled Foundation calculations (duplicate from above - needs cleanup)
  if (plot.foundationType === "piled" && plot.finishedFloorLevel) {
    if (!calculations.piledFoundation) {
      calculations.piledFoundation = {
        topOfBeamAsPiledGA: 0,
        masonryHeight: 0,
        standardConcreteDepth: 0,
        numberOfPiles: 0,
        rebar: 0,
        piledWalls: {
          external1100mm: 0,
          external500mm: 0,
          external450mm: 0,
          party750mm: 0,
          party500mm: 0,
          party450mm: 0,
          intWall500mm: 0,
          tieBeam400x500: 0,
          garExt500mm: 0,
          garExt450mm: 0,
          garInt500mm: 0,
          garInt450mm: 0,
        },
      };
    }

    // Top of Beam As piled GA = FFL - 0.75
    calculations.piledFoundation.topOfBeamAsPiledGA =
      plot.finishedFloorLevel - 0.75;

    // Masonry Height = FFL - Top of Beam As piled GA
    calculations.piledFoundation.masonryHeight =
      plot.finishedFloorLevel - calculations.piledFoundation.topOfBeamAsPiledGA;

    // Calculate Cordek Cellcore if wall measurements exist
    if (calculations.piledFoundation?.piledWalls) {
      const walls = calculations.piledFoundation.piledWalls;
      calculations.piledFoundation.cordekCellcore =
        walls.external1100mm * 1.1 +
        walls.external500mm * 0.5 +
        walls.external450mm * 0.45 +
        walls.party750mm * 0.75 +
        walls.party500mm * 0.5 +
        walls.party450mm * 0.45 +
        walls.intWall500mm * 0.5 +
        walls.tieBeam400x500 * 0.4 +
        walls.garExt500mm * 0.5 +
        walls.garExt450mm * 0.45 +
        walls.garInt500mm * 0.5 +
        walls.garInt450mm * 0.45;
    }
  }

  // Tanking calculations
  if (calculations.tanking && plot.additionalBlocks) {
    calculations.tanking.below =
      (plot.additionalBlocks || 0) + (plot.additionalBlocks || 0); // Standard value + Additional Block Value
    if (calculations.tanking.linM && calculations.tanking.above) {
      calculations.tanking.total =
        calculations.tanking.linM *
        (calculations.tanking.below + calculations.tanking.above);
    }
  }

  // Trenchfill Claymaster calculations
  if (
    plot.foundationType === "trenchfill" &&
    calculations.trenchfillClaymaster &&
    calculations.additionalConcrete
  ) {
    calculations.trenchfillClaymaster.depth =
      (calculations.additionalConcrete || 0) - 0.5;
    if (calculations.trenchfillClaymaster.linM) {
      calculations.trenchfillClaymaster.m2 =
        calculations.trenchfillClaymaster.linM *
        calculations.trenchfillClaymaster.depth;
    }
  }

  // Piled Claymaster calculations
  if (
    plot.foundationType === "piled" &&
    calculations.piledClaymaster &&
    calculations.piledFoundation
  ) {
    const walls = calculations.piledFoundation.piledWalls;
    calculations.piledClaymaster.linM =
      walls.external1100mm + walls.external500mm + walls.garExt500mm;
    calculations.piledClaymaster.depth =
      (calculations.piledFoundation.standardConcreteDepth || 0) + 0.05;
    calculations.piledClaymaster.m2 =
      calculations.piledClaymaster.linM * calculations.piledClaymaster.depth;
  }

  // Enhanced scaffold calculations
  if (plot.externalWalls && plot.garageExternalWalls) {
    const externalTotal = Object.values(plot.externalWalls || {}).reduce(
      (sum, val) => sum + (val || 0),
      0
    );
    const garExternalTotal = Object.values(
      plot.garageExternalWalls || {}
    ).reduce((sum, val) => sum + (val || 0), 0);

    // Default to detached house scaffolding (all around) if isTerraced is not specified
    calculations.scaffoldLength =
      plot.isTerraced === true
        ? (externalTotal + garExternalTotal) * 0.6 // Front and back only for terraced
        : externalTotal + garExternalTotal; // All around for detached (default)

    calculations.scaffoldPlatform = (externalTotal + garExternalTotal) * 2;

    if (calculations.plotArea) {
      calculations.pileMatt =
        calculations.scaffoldPlatform + calculations.plotArea;
    }
  }

  // Volume flags
  calculations.isHighCut = (calculations.cutVolume || 0) > 1000;
  calculations.isHighFill = (calculations.fillVolume || 0) > 1000;

  // Enhanced validation flags
  calculations.hasUnusualValues =
    (calculations.excavationDepth || 0) > 3 ||
    (calculations.excavationDepth || 0) < -2 ||
    (calculations.foundationDepth || 0) > 2 ||
    (calculations.foundationDepth || 0) < 0.5 ||
    (calculations.plotArea || 0) > 500 ||
    (calculations.plotArea || 0) < 50;

  return calculations;
};

// Simplified version that only calculates cut/fill volumes (for backwards compatibility)
export const calculateCutFillVolumes = (plot: PlotData): { cutVolume: number; fillVolume: number } => {
  // Use the full calculation function and extract only cut/fill values
  const calculated = calculatePlotValues(plot);
  return {
    cutVolume: calculated.cutVolume || 0,
    fillVolume: calculated.fillVolume || 0
  };
};